// ./client/components/Reports/Graphs.js
import React from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../context/appContext";
import { calcOptionTotal } from "../../utils/calcOptionTotal";
import { numberToMoney } from "../../utils/money";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

function BusinessReports() {
  ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

  const { reports, prices, loadMainRecords } = useAppContext();
  const navigate = useNavigate();

  const options = {
    responsive: true,
    interaction: {
      mode: "index",
      intersect: false,
    },
    stacked: false,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "FreeLite Metrics",
      },
    },
    scales: {
      x: { grid: { drawOnChartArea: reports.showXGrid } },
      y: {
        type: "linear",
        display: true,
        position: "left",
        ticks: {
          stepSize: 1,
        },
        grid: {
          drawOnChartArea: reports.showYGrid,
        },
      },
      y1: {
        type: "linear",
        display: true,
        position: "right",
        ticks: {
          callback: (value) => numberToMoney(value, false),
        },
        grid: {
          drawOnChartArea: reports.showY1Grid,
        },
      },
    },
  };

  const dayMilSecs = 1000 * 60 * 60 * 24;
  const cssQtyLine = "#4d93ff";
  const cssQtyDot = "#005be6";
  const cssValLine = "#00b063";
  const cssValDot = "#0f5132";
  const cssQtyLine1 = "#ff7f50";
  const cssQtyDot1 = "#e65c33";
  const cssValLine1 = "#008080";
  const cssValDot1 = "#006666";
  // const cssQtyLine2 = "#1a5abb";  // Darker blue line
  // const cssQtyDot2 = "#003d82";   // Darker blue dot
  // const cssValLine2 = "#008048";  // Darker green line
  // const cssValDot2 = "#00331a";    // Darker green dot
  // const intervalDaily = 0;
  const intervalWeekly = 1;
  const intervalMonthly = 2;
  // const intervalQuarterly = 3;
  // const typeEstimates = 1;
  // const typeCompleted = 2;
  // const typePayments = 3;
  const typeVeluxJobs = 5;

  const termLength = () => {
    const startDate = new Date(reports.startDate);
    const endDate = new Date(reports.endDate);
    const diffTime = endDate - startDate;

    return Math.ceil(diffTime / dayMilSecs);
  };

  const getTermDays = (yearsOffset = 0) =>
    Array.from({ length: termLength() }, (_, i) => i + 1).map((day) => {
      const adjustedStartDate = offsetDate(reports.startDate, yearsOffset);
      const date = new Date(adjustedStartDate);
      date.setDate(date.getDate() + day);
      return date.toISOString();
    });

  const offsetDate = (dateStr, years) => {
    const d = new Date(dateStr);
    d.setFullYear(d.getFullYear() - years);
    return d.toISOString().split("T")[0];
  };

  // Gets Appointments or Payments and filters based on date and job type
  const getRecords = (yearsOffset = 0) => {
    let records = [];
    let appts = reports.appointments;
    const adjustedStartDate = offsetDate(reports.startDate, yearsOffset);
    const adjustedEndDate = offsetDate(reports.endDate, yearsOffset);
    console.log("startDate:", reports.startDate);
    console.log("adjustedStartDate:", adjustedStartDate);
    // Filter appointments for estimates
    if (reports.recordsType === 0) {
      appts = appts?.filter((appt) => appt.appt_types_id === 1);
    }
    // Filter appointments for scheduled work
    else if (reports.recordsType === 1) {
      appts = appts?.filter((appt) => appt.appt_types_id === 2);
    }
    // If receipts selected return payments in date range
    if (reports.recordsType === 2) {
      records = reports.payments?.filter(
        (payment) =>
          payment.payment_date >= adjustedStartDate && payment.payment_date <= adjustedEndDate
      );
    }
    // Otherwise return appointments within date range
    else {
      records = appts?.filter((appt) => {
        const apptDate = appt.appt_date?.split("T")[0].split(" ")[0];
        return apptDate >= adjustedStartDate && apptDate <= adjustedEndDate;
      });
    }

    // Filter records based on Job Type
    console.log("records filtered by date: ", records);
    return records?.filter((rec) => {
      const jobType = reports.jobs?.filter((job) => job.id === rec.core_job_data_id)[0]
        ?.job_type_id;
      // Filter based on job type
      if (reports.includeService && jobType === 1) {
        return true;
      }
      if (reports.includeInstall && jobType === 3) {
        return true;
      }
      if (reports.includeCommercial && jobType === 4) {
        return true;
      }
    });
  };

  // Calculate the total value of the job associated with each record
  const calcValue = (crntJob, crntOption) => {
    if (!crntJob || !crntOption) {
      console.log("crntJob:", crntJob);
      console.log("crntOption:", crntOption);
      return;
    }
    const jobItems = reports.jobItems.filter((item) => item.options_id === crntOption.id);
    const fees = reports.fees.filter((fee) => fee.options_id === crntOption.id);
    const discounts = reports.discounts.filter((dsct) => dsct.options_id === crntOption.id);

    const totals = calcOptionTotal({
      jobItems,
      discounts,
      fees,
      sales_tax: crntOption.sales_tax,
      job: crntJob,
      prices,
    });

    // console.log("totals:", totals.grandTotal);
    return totals.grandTotal;
  };

  // -------- The Main Data Compiling Function ---------
  const fillData = (records, yearsOffset = 0) => {
    let labels = [];
    let quantity = [];
    let value = [];
    let largestJob = { amount: 0 };
    let termQty = 0;
    let termVal = 0;
    let termNumber;

    console.log("records filtered by job type: ", records);

    // Loop through each day in the report and calculate
    const termDays = getTermDays(yearsOffset);
    termDays.forEach((day, i) => {
      const date = new Date(day);
      // console.log("day: ", day);
      const dayAppts = records?.filter((record) => {
        const formattedDay = day?.split("T")[0].split(" ")[0];
        const formattedAppt = record.appt_date?.split("T")[0].split(" ")[0];
        const formattedPayment = record.payment_date?.split("T")[0].split(" ")[0];

        return formattedAppt === formattedDay || formattedPayment === formattedDay;
      });

      let dayQty = dayAppts?.length || 0;

      // Find job totals
      const dayVal = dayAppts?.reduce((ttl, currentAppt) => {
        const currentJob = reports.jobs?.filter(
          (job) => job.id === currentAppt.core_job_data_id
        )[0];
        const currentOptions = reports.options?.filter(
          (opt) => opt.core_job_data_id === currentAppt.core_job_data_id
        );
        const chosenOption = currentOptions.filter((opt) => opt.id === currentJob.options_id)[0];

        // Determine value for estimates with multiple options
        if (currentOptions.length > 1) {
          // If chosen option not present then take an average value
          if (!chosenOption) {
            let ttlVal = 0;
            let qty = 0; // keep track to remove custom price estimates
            currentOptions.forEach((opt) => {
              const val = calcValue(currentJob, opt);
              if (val) {
                qty = qty + 1;
                ttlVal = ttlVal + val;
              }
            });
            // If none of the estimates have a valid price
            if (qty === 0) {
              console.log(
                `Not counting this multi-option appointment (${currentAppt.id})! Daily quantity: `,
                dayQty
              );
              dayQty = dayQty - 1; // remove from day's quantity count
              return ttl; // don't change the total value
            }

            return ttl + ttlVal / qty;
          }
        }
        // Otherwise use single value for daily total
        const val = calcValue(currentJob, chosenOption || currentOptions[0]);
        // Don't count if custom price
        if (!val) {
          console.log(
            `Not counting this appointment (${currentAppt.id})! Daily quantity: `,
            dayQty
          );
          dayQty = dayQty - 1; // remove from day's quantity count
          return ttl; // don't change the total value
        }
        // Grab largest job info
        if (val > largestJob.amount) {
          const amount = val;
          const jobId = currentJob.id;
          const jobNumber = currentJob.job_number;
          largestJob = { amount, jobId, jobNumber };
          console.log("largestJob:", largestJob);
        }
        return ttl + val;
      }, 0);

      // Compile monthly data
      if (Number(reports.interval) === intervalMonthly) {
        termQty = termQty + dayQty;
        termVal = termVal + dayVal;
        // Start tracking for first month
        if (i === 0) {
          labels.push(date.toDateString().slice(4, -5));
          termNumber = date.getMonth();
        }
        // If it changes add and reset variables
        else if (date.getMonth() !== termNumber) {
          labels.push(date.toDateString().slice(4, -5));
          quantity.push(termQty);
          value.push(termVal);
          termQty = 0;
          termVal = 0;
          termNumber = date.getMonth();
        }
        // Add data from last month
        else if (i === termDays.length - 1) {
          quantity.push(termQty);
          value.push(termVal);
        }
      }

      // Or compile weekly data if desired
      else if (Number(reports.interval) === intervalWeekly) {
        termQty = termQty + dayQty;
        termVal = termVal + dayVal;
        // Start tracking for first week
        if (i === 0) {
          labels.push(date.toDateString().slice(0, -5));
        }
        // If week starts over
        else if (i % 7 === 0) {
          labels.push(date.toDateString().slice(0, -5));
          quantity.push(termQty);
          value.push(termVal);
          termQty = 0;
          termVal = 0;
        }
        // Add data from last week
        else if (i === termDays.length - 1) {
          quantity.push(termQty);
          value.push(termVal);
        }
      }

      // Otherwise return daily data
      else {
        labels.push(date.toDateString().slice(0, -5));
        quantity.push(dayQty);
        // console.log("dayVal:", dayVal)
        value.push(dayVal);
      }
    });

    return { labels, quantity, value, largestJob };
  };

  const yearsOffset = 1;
  const records = getRecords();
  const previousRecords = getRecords(yearsOffset);
  let data, previousData, dataVelux, previousDataVelux;

  data = fillData(records);
  previousData = fillData(previousRecords, yearsOffset);

  // Filter the records list down to only jobs with velux skylights
  if (reports.reportTypesId === typeVeluxJobs) {
    const getFilteredRecords = (records) =>
      records.filter((rec) => {
        const currentJob = reports.jobs?.filter((job) => job.id === rec.core_job_data_id)[0];
        const currentOptions = reports.options?.filter(
          (opt) => opt.core_job_data_id === rec.core_job_data_id
        );
        const currentOption = currentOptions.filter((opt) => opt.id === currentJob.options_id)[0];
        const currentJobItems =
          currentOption && reports.jobItems.filter((item) => item.options_id === currentOption.id);

        // test if any of the job items has a velux frame
        const veluxFrameIds = [5, 6, 7, 9, 10]; // 5 reg sun tnl, 6 low-profile st, 7 impact st, 9 curbmount, 10 deckmount
        if (currentJobItems)
          return currentJobItems?.some((jobItems) =>
            veluxFrameIds.includes(jobItems.frame_types_id)
          );
      });
    const filteredRecords = getFilteredRecords(records);
    dataVelux = filteredRecords.length > 0 && fillData(filteredRecords);
    const filteredPreviousRecords = getFilteredRecords(previousRecords);
    previousDataVelux = filteredPreviousRecords.length > 0 && fillData(filteredPreviousRecords);
  }

  const relevantData = dataVelux?.value?.length ? dataVelux : data;
  const getStats = (relevantData, title = "Base Statistics") => {
    const totalQty = relevantData?.quantity.reduce((a, c) => a + c, 0);
    const totalValue = relevantData?.value.reduce((a, c) => a + c, 0);
    const maxValue = relevantData?.largestJob.amount;
    const maxJobNum = relevantData?.largestJob.jobNumber;
    const dataLength = relevantData?.labels?.length;

    return { title, relevantData, totalQty, totalValue, maxValue, maxJobNum, dataLength };
  };
  const baseStats = data && getStats(data);
  const previousBaseStats = reports.includePrevious && previousData && getStats(previousData, "Previous Year Statistics");
  const veluxStats = dataVelux && getStats(dataVelux, "VELUX Statistics");
  const previousVeluxStats =
    reports.includePrevious &&
    previousDataVelux &&
    getStats(previousDataVelux, "Previous Year VELUX Statistics");

  const datasetConfigs = [
    // Quantity datasets
    {
      condition: reports.showQuantity,
      label: `Quantity`,
      data: data?.quantity,
      borderColor: cssQtyLine,
      backgroundColor: cssQtyDot,
      yAxisID: "y",
    },
    {
      condition: reports.showQuantity && reports.includePrevious,
      label: "Quantity (Previous Year)",
      data: previousData?.quantity,
      borderColor: cssQtyLine,
      backgroundColor: cssQtyDot,
      borderDash: [5, 5],
      yAxisID: "y",
    },
    {
      condition: reports.showQuantity && reports.reportTypesId === typeVeluxJobs,
      label: `Quantity With VELUX`,
      data: dataVelux?.quantity,
      borderColor: cssQtyLine1,
      backgroundColor: cssQtyDot1,
      yAxisID: "y",
    },
    {
      condition:
        reports.showQuantity && reports.reportTypesId === typeVeluxJobs && reports.includePrevious,
      label: "Quantity With VELUX (Previous Year)",
      data: previousDataVelux?.quantity,
      borderColor: cssQtyLine1,
      backgroundColor: cssQtyDot1,
      borderDash: [5, 5],
      yAxisID: "y",
    },

    // Value datasets
    {
      condition: reports.showValue,
      label: `Total Value`,
      data: data?.value,
      borderColor: cssValLine,
      backgroundColor: cssValDot,
      yAxisID: "y1",
    },
    {
      condition: reports.showValue && reports.includePrevious,
      label: "Value (Previous Year)",
      data: previousData?.value,
      borderColor: cssValLine,
      backgroundColor: cssValDot,
      borderDash: [5, 5],
      yAxisID: "y1",
    },
    {
      condition: reports.showValue && reports.reportTypesId === typeVeluxJobs,
      label: `Total Value With VELUX`,
      data: dataVelux?.value,
      borderColor: cssValLine1,
      backgroundColor: cssValDot1,
      yAxisID: "y1",
    },
    {
      condition:
        reports.showValue && reports.reportTypesId === typeVeluxJobs && reports.includePrevious,
      label: "Value With VELUX (Previous Year)",
      data: previousDataVelux?.value,
      borderColor: cssValLine1,
      backgroundColor: cssValDot1,
      borderDash: [5, 5],
      yAxisID: "y1",
    },
  ];

  const myData = {
    labels: data?.labels,
    datasets: datasetConfigs
      .filter((config) => config.condition)
      .map(({ condition, ...rest }) => ({ ...rest, tension: 0.2 })),
  };

  const loadLargestJob = () => {
    loadMainRecords(relevantData?.largestJob.jobId);
    navigate("../job-summary");
  };

  return (
    <div className="whole graphs">
      {console.log("data:", data)}
      {!data?.labels.length === 0 || !data?.quantity.length === 0 || !data?.value.length === 0 ? (
        <div>Loading...</div>
      ) : (
        <>
          {/* ----- Graphs ------ */}
          {data?.value?.length > 0 && <Line key={data?.value[0]} options={options} data={myData} />}

          {/* ----- Totals ----- */}
          <div className="whole flex-parent">
            {[baseStats, previousBaseStats, veluxStats, previousVeluxStats].map((data, i) => {
              if (data) {
                const { title, totalQty, totalValue, maxValue, maxJobNum, dataLength } = data;
                return (
                  <div className="form-row report-totals one-half" key={i}>
                    <br />
                    <h3>{title}</h3>
                    Total Quantity: {totalQty}
                    <br />
                    Average Quantity per Term: {(totalQty / dataLength).toFixed(1)}
                    <br />
                    Total Value: {numberToMoney(totalValue)}
                    <br />
                    Average Value per Term: {numberToMoney(totalValue / dataLength)}
                    <br />
                    Largest Job: {numberToMoney(maxValue)} -{" "}
                    <a href="#" onClick={loadLargestJob}>
                      {maxJobNum}
                    </a>
                    {/*
              <br />
              Total Profit/Expected Profit: (coming soon)
              <br />
              Average Profit: (coming soon)
              <br />
              Most Profitable Job: (coming soon)
              <br />
              Terms: {dataLength} ({dataLength - 1} complete terms and 1 partial)
              */}
                  </div>
                );
              }
            })}
          </div>
        </>
      )}
    </div>
  );
}
export default BusinessReports;
